import { useState, useEffect, useRef } from 'react';

export default function Services() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [headerVisible, setHeaderVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeaderVisible(true);
            headerObserver.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    const cardsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('[data-card-index]');
            cards.forEach((_, index) => {
              setTimeout(() => {
                setVisibleCards((prev) =>
                  prev.includes(index) ? prev : [...prev, index]
                );
              }, index * 200);
            });
            cardsObserver.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }
    if (sectionRef.current) {
      cardsObserver.observe(sectionRef.current);
    }

    return () => {
      headerObserver.disconnect();
      cardsObserver.disconnect();
    };
  }, []);

  const services = [
    {
      icon: 'ri-shirt-line',
      title: 'Žehlení košil',
      description: 'Profesionální žehlení košil s péčí o každý detail',
      price: 'od 55 Kč',
      color: 'teal',
    },
    {
      icon: 'ri-t-shirt-line',
      title: 'Žehlení oděvů',
      description: 'Komplexní péče o všechny typy oděvů',
      price: 'od 45 Kč',
      color: 'teal',
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Ložní prádlo',
      description: 'Žehlení povlečení, prostěradel a dalšího prádla',
      price: 'od 45 Kč',
      color: 'teal',
    },
    {
      icon: 'ri-scissors-line',
      title: 'Opravy oděvů',
      description: 'Profesionální opravy a úpravy oděvů',
      price: 'od 110 Kč',
      color: 'teal',
    },
  ];

  return (
    <section id="sluzby" className="py-12 sm:py-20 bg-white dark:from-gray-900 dark:to-gray-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Animated Header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16">
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 transition-all duration-700 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-8'
            }`}
          >
            Naše služby
          </h2>

          {/* Animated decorative line */}
          <div className="flex justify-center mb-6">
            <div
              className={`h-1 rounded-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-700 ease-out ${
                headerVisible ? 'w-20 opacity-100' : 'w-0 opacity-0'
              }`}
              style={{ transitionDelay: '300ms' }}
            />
          </div>

          <p
            className={`text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto transition-all duration-700 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            Nabízíme komplexní péči o vaše oblečení s důrazem na kvalitu a detail
          </p>
        </div>

        {/* Animated Cards */}
        <div ref={sectionRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              data-card-index={index}
              className={`bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl cursor-pointer group transition-all duration-600 ease-out ${
                visibleCards.includes(index)
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{
                transitionDuration: '600ms',
                transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-2xl mb-4 sm:mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 bg-teal-100 dark:bg-teal-900/30"
              >
                <i
                  className={`${service.icon} text-2xl sm:text-3xl text-teal-600`}
                ></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 transition-colors duration-300 group-hover:text-teal-600">
                {service.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 text-sm leading-relaxed">
                {service.description}
              </p>
              <p className="text-base sm:text-lg font-bold text-teal-600">
                {service.price}
              </p>

              {/* Animated bottom accent line */}
              <div className="mt-4 h-0.5 rounded-full bg-gradient-to-r from-teal-400 to-teal-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
